import { useEffect, useRef } from 'react'

interface Options<T> {
  items: T[]
  /** 편집/작업 모달이 열려있을 때 true — 리스트 keydown 무시. */
  disabled?: boolean
  onEnter?: (item: T, idx: number) => void
  onDelete?: (item: T, idx: number) => void
  /** 자동 focus 활성 여부. 기본 true. */
  autoFocusFirst?: boolean
  /** 가상화 리스트용 — 해당 idx 를 화면에 스크롤(=렌더)시키는 콜백. 주면 focusRow 가
   *  DOM 에 없는(오프스크린) 행도 스크롤 후 재시도해 포커스한다. */
  ensureVisible?: (idx: number) => void
}

/** 리스트 <tbody> 에 ↑↓/Enter/Del 네비게이션을 붙이는 공용 hook.
 *
 * 사용:
 *   const { tbodyRef, onKeyDown } = useListNavigation({
 *     items: rows,
 *     disabled: !!edit,
 *     onEnter: (r) => openEdit(r),
 *     onDelete: (r) => confirm(`삭제?`) && remove.mutate(r.id),
 *   })
 *   ...
 *   <tbody ref={tbodyRef} onKeyDown={onKeyDown}>
 *     {rows.map((r, idx) => (
 *       <tr data-idx={idx} tabIndex={idx === 0 ? 0 : -1}
 *           className="focus:bg-blue-50 focus:outline-none">...
 *     ))}
 *   </tbody>
 */
export function useListNavigation<T>({
  items, disabled = false, onEnter, onDelete, autoFocusFirst = true, ensureVisible,
}: Options<T>) {
  const tbodyRef = useRef<HTMLTableSectionElement>(null)

  function focusRow(idx: number, auto = false) {
    ensureVisible?.(idx)   // 가상화: 오프스크린 행을 먼저 스크롤해 렌더시킴
    let tries = 0
    const attempt = () => {
      // 자동 focus 는 사용자가 입력칸(검색창 등)에 치고 있으면 건너뜀 — 검색 결과가 올 때마다 첫 줄로
      // focus 를 뺏어 이어 치는 글자가 사라졌음(거래처관리 검색, 고객 지적 2026-10-20).
      const ae = document.activeElement
      if (auto && ae && ['INPUT', 'TEXTAREA', 'SELECT'].includes(ae.tagName)) return
      const tr = tbodyRef.current?.querySelector<HTMLElement>(`tr[data-idx="${idx}"]`)
      if (tr) { tr.focus(); tr.scrollIntoView({ block: 'nearest' }) }
      else if (tries++ < 6) requestAnimationFrame(attempt)  // 렌더 대기 후 재시도
    }
    requestAnimationFrame(attempt)
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (disabled) return
    const el = e.target instanceof HTMLElement ? e.target : null
    // 행 내부 input/button 이벤트는 무시 (인라인 인터랙션 우선)
    if (el?.tagName === 'INPUT' || el?.tagName === 'BUTTON' || el?.tagName === 'SELECT'
        || el?.tagName === 'TEXTAREA') return
    const tr = el?.closest<HTMLElement>('tr[data-idx]')
    if (!tr) return
    const idx = parseInt(tr.getAttribute('data-idx') || '-1', 10)
    if (idx < 0 || idx >= items.length) return

    if (e.key === 'ArrowDown') { e.preventDefault(); if (idx + 1 < items.length) focusRow(idx + 1) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); if (idx > 0) focusRow(idx - 1) }
    else if (e.key === 'Enter') { e.preventDefault(); onEnter?.(items[idx], idx) }
    else if (e.key === 'Delete') { e.preventDefault(); onDelete?.(items[idx], idx) }
  }

  // 자동 focus (리스트 로딩되거나 모달 닫힐 때)
  useEffect(() => {
    if (!autoFocusFirst) return
    if (disabled) return
    if (items.length > 0) focusRow(0, true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length, disabled, autoFocusFirst])

  return { tbodyRef, onKeyDown, focusRow }
}
